import React from 'react';

import { CaraWorkspaceFileInput } from '~/api/graphql';

/**
 * Helper function to extract File[] from CaraWorkspaceFileInput[] for display purposes
 */
export const extractUploadFiles = (files: CaraWorkspaceFileInput[]): File[] => {
  return files.filter((f) => 'upload' in f && f.upload).map((f) => f.upload!);
};

/**
 * Simulated upload progress UI used while a workspace creation request is in
 * flight. The animation is purely cosmetic — it estimates progress based on a
 * fixed assumed upload rate so the user has feedback while the mutation runs.
 */
export const AnimatedUploadProgress = (props: { files: File[] }) => {
  const [uploadState, setUploadState] = React.useState<{
    fileIndex: number;
    fileBytesUploaded: number;
  }>({ fileIndex: 0, fileBytesUploaded: 0 });

  const totalBytes = props.files.reduce((acc, file) => acc + file.size, 0);
  const totalBytesUploaded =
    props.files.slice(0, uploadState.fileIndex).reduce((acc, file) => acc + file.size, 0) +
    uploadState.fileBytesUploaded;

  // Upload rate, assume 1 MB/s (8 Mbps)
  const uploadRateBytesPerSecond = 1 * 1024 * 1024;

  React.useEffect(() => {
    if (props.files.length === 0) {
      return;
    }

    setUploadState({
      fileIndex: 0,
      fileBytesUploaded: 0
    });

    const interval = setInterval(() => {
      setUploadState((prev) => {
        // Safety check: ensure fileIndex is within bounds (handles race conditions)
        if (prev.fileIndex >= props.files.length || prev.fileIndex < 0) {
          clearInterval(interval);
          return {
            fileIndex: props.files.length - 1,
            fileBytesUploaded: props.files[props.files.length - 1]?.size || 0
          };
        }

        const currentFile = props.files[prev.fileIndex];
        const currentFileSize = currentFile.size;

        // Check if we're on the last file and it's complete
        if (
          prev.fileIndex === props.files.length - 1 &&
          prev.fileBytesUploaded >= currentFileSize
        ) {
          clearInterval(interval);
          return {
            fileIndex: prev.fileIndex,
            fileBytesUploaded: currentFileSize
          };
        }

        // Move to next file if current one is complete
        if (prev.fileBytesUploaded >= currentFileSize) {
          const nextIndex = prev.fileIndex + 1;
          if (nextIndex >= props.files.length) {
            clearInterval(interval);
            return {
              fileIndex: props.files.length - 1,
              fileBytesUploaded: props.files[props.files.length - 1]?.size || 0
            };
          }

          return {
            fileIndex: nextIndex,
            fileBytesUploaded: 0
          };
        }

        // Calculate bytes to upload for current file
        const bytesToUpload =
          0.5 + 0.25 * Math.ceil(Math.random() * 4) * (uploadRateBytesPerSecond / 10);

        return {
          fileIndex: prev.fileIndex,
          fileBytesUploaded: Math.min(prev.fileBytesUploaded + bytesToUpload, currentFileSize)
        };
      });
    }, 100);

    return () => clearInterval(interval);
  }, [props.files.length, ...props.files.map((f) => f.size)]);

  if (props.files.length === 0) {
    return null;
  }

  // Safety check for current file (handles race conditions)
  const currentFile = props.files[uploadState.fileIndex];
  if (!currentFile) {
    return null;
  }

  const currentFileSize = currentFile.size;
  const currentFileProgress =
    currentFileSize > 0 ? uploadState.fileBytesUploaded / currentFileSize : 0;

  return (
    <div className="mt-4 flex flex-col gap-y-1">
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        Uploading {currentFile.name} (
        {new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(currentFileProgress)}
        )...
      </div>
      <div className="h-2 w-full rounded-full bg-neutral-100 dark:bg-neutral-700">
        <div
          className={`h-2 rounded-full bg-gradient-to-r to-primary-300 dark:to-primary-500/30 from-primary-600 bg-[size:200%_100%] animate-progress-bar transition-all ease-in-out duration-300`}
          style={{ width: `${totalBytes > 0 ? (totalBytesUploaded / totalBytes) * 100 : 100}%` }}
        />
      </div>
    </div>
  );
};
